import React, { useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { Img } from 'react-image';
import pluralize from 'pluralize';

import { brandNeutralColors, colors } from 'ui_system/compass';
import { DEFAULT_OPTS as SPINNER_OPTIONS } from 'views/anyguide/helpers/spinner';
import EditIcn from 'views/ui/icons/edit-icon';
import BackIcon from 'views/ui/icons/back-icon';
import AvailabilityRuleIcon from 'views/ui/icons/availability-rule-icon';
import LocationIcn from 'views/ui/icons/location-icon';
import ComputerIcn from 'views/ui/icons/computer';

import { cssOverflowEllipsis } from 'views/anyguide/messages/styles';
import { Theme, Grid } from 'views/anyguide/tours/index/TourListStyles';
import defaultImage from 'views/anyguide/tours/item/images/default_image.png';
import { t } from 'services/translation';

import ContentLoader from 'ui_system/utilities/ContentLoader';
import Popover from 'ui_system/popovers/Popover';

import {
  createScheduleSelected,
  cancelledCreateSchedule,
} from 'data_system/resource_portal/actions/experienceActions';

import {
  getAvailabilityModalVisible,
  getSelectedExperienceId,
} from 'data_system/resource_portal/selectors';

import { ScheduleContainerModal } from './schedule/ScheduleContainer';

const { tinyMobileBreakpointWidth } = Theme.mobile;

const themeLinkColor = (props) => props.theme.linkColor || colors.text.link;

const CovidMessage = styled.div`
  background-color: #333333;
  color: white;
  font-weight: 400;
  font-size: 14px;
  padding: 10px 20px;
  padding-bottom: 30px;
  max-width: 250px;
  div {
    text-align: left;
    color: white;
    padding-bottom: 10px;
  }
`;

const Card = styled(Grid)`
  align-items: start;
  justify-content: space-between;
  padding: 20px 0;
  font-size: 14px;
  border-bottom: 1px solid ${brandNeutralColors.border};
  height: 150px;
  opacity: ${(card) => (card.fade ? '0.4' : '1')};

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    height: auto;
    grid-template-columns: 90px 1fr;
    grid-template-rows: 80px 1fr;
    font-size: 13px;
    padding-bottom: 10px;
    padding-top: 16px;
  }

  @media (max-width: ${tinyMobileBreakpointWidth}px) {
    padding: 10px 0px;
    font-size: 12px;
    width: 90vw;
  }
`;

export const ComputerIcon = styled(ComputerIcn)`
  fill: ${brandNeutralColors.text.icon};
  height: 14px;
  position: relative;
  top: 5px;
  margin-right: 2px;
  @media (min-width: ${tinyMobileBreakpointWidth}px) {
    height: 16px;
  }
`;

const TourImage = styled(Img)`
  width: 109px;
  height: 109px;
  margin-right: 20px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    height: 81px;
    width: 81px;
  }
`;

// ==== ACTION LINKS
const Actions = styled.div`
  display: flex;
  div:focus {
    outline: 0;
  }
`;

const LocationIcon = styled(LocationIcn)`
  fill: ${brandNeutralColors.text.icon};
  height: 18px;
  width: 18px;
  position: relative;
  top: 5px;
  margin-right: 2px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    height: 16px;
    width: 16px;
  }
`;

const ActionSection = styled.div`
  height: 100%;
  justify-content: end;
  display: grid;
  align-items: center;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    justify-content: end;
    grid-column: span 2;
    display: grid;
  }

  @media (max-width: ${tinyMobileBreakpointWidth}px) {
    padding: 0px;
  }
`;

const ActionSectionLinks = styled(ActionSection)`
  align-items: end;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    border-top: solid 1px ${brandNeutralColors.border};
    display: block;
    padding-top: 10px;
    margin-top: 16px;
  }
`;

export const LoadingOverlay = styled.div`
  height: 100vh;
  width: 100vw;
  background: #fff;
  opacity: 0.5;
  position: absolute;
  z-index: 2;
`;

const previewEnabledTourUrl = (partnerSlug, tour) => {
  const { id, slug: tourSlug, isOnline, commerceEnabled, userId } = tour;

  // Michaels-Specific ===
  const offlinePreviewUrl = '/classes-and-events/classroom#id=';
  const onlinePreviewUrl = '/communityonline#id=';
  const previewUrl = isOnline ? onlinePreviewUrl : offlinePreviewUrl;

  if (partnerSlug === 'michaelsstore') {
    return `//www.michaels.com${previewUrl}${tourSlug}`;
  }

  // Disabled until Michaels Canada goes live with their online experiences page
  // else if (partnerSlug === 'michaelsstore-canada') {
  //   return `//www.michaels.ca${previewUrl}${tourSlug}`;
  // }
  // === Michaels-Specific

  return `preview/${id}`;
};

const LocationInfo = styled.span`
  color: #999;
  line-height: 20px;
`;

const TourInfo = styled.div`
  width: 100%;
`;

const TourName = styled.div`
  display: block;
  font-size: 16px;
  ${cssOverflowEllipsis} @media (max-width: 835px) {
    max-width: 340px;
  }
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    font-size: 15px;
  }
`;

const ActionViewHelpCenter = styled.a`
  color: white;
  text-decoration: underline;
  float: right;
`;

const ActionContainer = styled.div`
  padding-top: ${(props) =>
    props.paddingTop ? `${props.paddingTop}px` : '0px'};
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    padding-top: 0px;
  }
`;

const EditActionContainer = styled.div`
  padding-top: 3.5px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    padding-top: 5.5px;
  }
`;

const greenFillIcon = (props) => css`
  fill: ${props.theme.linkColor || Theme.colors.green};
  margin-right: 10px;
  height: 18px;
  width: 18px;
`;

const EditIcon = styled(EditIcn)`
  ${greenFillIcon};
  position: relative;
  top: 2px;
  height: 11px;
  width: 11px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    margin-right: 7px;
    height: 11px;
    width: 11px;
  }
`;

const PreviewIcon = styled(BackIcon)`
  ${greenFillIcon};
  color: ${themeLinkColor};
  top: 2px;
  right: -2px;
  transform: rotate(135deg);
  position: relative;
  width: 18px;
  height: 18px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    width: 18px;
    height: 18px;
    margin-right: 7px;
  }
`;

const StyledAvailabilityRuleIcon = styled(AvailabilityRuleIcon)`
  ${greenFillIcon};
  color: ${themeLinkColor};
  outline: ${themeLinkColor};
  top: 3px;
  right: -2px;
  position: relative;
  width: 15px;
  height: 15px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    width: 18px;
    height: 18px;
    margin-right: 7px;
  }
`;

const StepsRemainingButton = styled(Link)`
  background-color: white;
  border: 1px solid ${themeLinkColor};
  border-radius: 4px;
  color: ${themeLinkColor};
  font-size: 14px;
  height: 44px;
  min-width: 140px;
  padding: 12px;
  text-align: center;

  :hover {
    color: ${themeLinkColor};
  }
`;

const ActionEditLink = styled(Link)`
  color: ${themeLinkColor};
  padding-left: 13px;

  :hover {
    color: ${themeLinkColor};
  }
`;

const ActionPreviewLink = styled.a`
  border-right: solid 1px ${brandNeutralColors.border};
  border-left: solid 1px ${brandNeutralColors.border};
  color: ${themeLinkColor};
  padding-right: 13px;
  padding-left: 10px;

  :hover {
    color: ${themeLinkColor};
  }
`;

const ActionScheduleLink = styled(ActionPreviewLink)`
  border-left: 0px;
  border-right: 0px;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? '0.3' : '1')};
  padding-right: 15px;
`;

const ExperienceCard = (props) => {
  const { loading, fade, tour, brandSlug, isOnlineExperiencesAllowed } = props;

  const [scheduleClassTooltip, setScheduleClassTooltip] = useState(false);

  const dispatch = useDispatch();
  const availabilityModalVisible = useSelector(getAvailabilityModalVisible);

  const selectedExperienceId = useSelector(getSelectedExperienceId);

  const renderLoadingPlaceholder = () => {
    return (
      <Card gridTemplateColumns="130px 1fr 1fr">
        <ContentLoader
          height={109}
          width={109}
          style={{ marginRight: '20px' }}
        />
        <TourInfo>
          <ContentLoader
            height={18}
            width={230}
            style={{ marginBottom: '10px', display: 'block' }}
          />
          <ContentLoader height={18} width={280} />
        </TourInfo>
        <ActionSection>
          <ContentLoader height={16} width={120} />
          <ContentLoader height={16} width={180} />
        </ActionSection>
      </Card>
    );
  };

  const isSchedulable = (guideId, isOnline) => {
    // hardcode for michael resource accounts
    // if ([42253, 42446].includes(guideId)) {
    //   return isOnline && isOnlineExperiencesAllowed;
    // }

    return true;
  };

  const renderScheduleButton = () => {
    const { id, isOnline, userId } = tour;

    return (
      <ActionContainer paddingTop={3}>
        {isSchedulable(userId, isOnline) ? (
          <ScheduleContainerModal
            key={`ScheduleContainerModal${id}`}
            open={selectedExperienceId === id && availabilityModalVisible}
            onOpen={() => {
              dispatch(createScheduleSelected(id));
            }}
            onClose={() => {
              dispatch(cancelledCreateSchedule(id));
            }}
            width="520px"
          >
            <ActionScheduleLink>
              <StyledAvailabilityRuleIcon />
              {t('SCHEDULE_CLASS')}
            </ActionScheduleLink>
          </ScheduleContainerModal>
        ) : (
          <Popover
            boundary="window"
            placement="bottom-start"
            content={
              <CovidMessage>
                <div>
                  Michaels is pausing scheduling of classes for the Community
                  Classroom program until further notice.
                </div>
                <ActionViewHelpCenter
                  target="_blank"
                  href="https://michaels-instructors.helpscoutdocs.com/article/571-community-classroom-update"
                >
                  Learn more
                </ActionViewHelpCenter>
              </CovidMessage>
            }
            visible={scheduleClassTooltip}
            theme="dark"
            onHidden={() => setScheduleClassTooltip(false)}
          >
            <div>
              <ActionScheduleLink
                disabled
                onClick={() => setScheduleClassTooltip(!scheduleClassTooltip)}
              >
                <StyledAvailabilityRuleIcon />
                {t('SCHEDULE_CLASS')}
              </ActionScheduleLink>
            </div>
          </Popover>
        )}
      </ActionContainer>
    );
  };

  const stepsRemaining = () => {
    const { resourcePortalStepsProgress } = tour;
    const steps = resourcePortalStepsProgress.filter((step) => !step.done);
    return {
      navToStep: steps.length > 0 ? steps[0].name : 'about',
      number: steps.length,
    };
  };

  const renderActionSection = () => {
    const { id, isEnabled } = tour;

    const steps = stepsRemaining();
    const editUrl = `/experiences/${id}/edit/${steps.navToStep}`;

    SPINNER_OPTIONS.radius = 8;

    if (steps.number === 0) {
      return (
        <ActionSectionLinks>
          <Actions>
            {renderScheduleButton()}
            <ActionContainer>
              <ActionPreviewLink
                target="_blank"
                href={
                  isEnabled
                    ? previewEnabledTourUrl(brandSlug, tour)
                    : `preview/${id}`
                }
              >
                <PreviewIcon />
                {t('PREVIEW')}
              </ActionPreviewLink>
            </ActionContainer>
            <EditActionContainer>
              <ActionEditLink to={editUrl}>
                <EditIcon />
                {t('EDIT')}
              </ActionEditLink>
            </EditActionContainer>
          </Actions>
        </ActionSectionLinks>
      );
    }

    // Unfinished steps state, renders a button instead
    return (
      <ActionSection>
        <StepsRemainingButton to={editUrl}>
          {steps.number} {t(`${pluralize('steps', steps.number)} remaining`)}
        </StepsRemainingButton>
      </ActionSection>
    );
  };

  if (loading) return renderLoadingPlaceholder();

  const { picture, name, nameAndAddress, isOnline } = tour;
  const locationLabel = isOnline ? t('ONLINE') : nameAndAddress;
  const placeIcon = isOnline ? <ComputerIcon /> : <LocationIcon />;
  return (
    <Card gridTemplateColumns="130px 1fr 1fr" key={tour.id} fade={fade}>
      <TourImage
        src={picture}
        loader={
          <ContentLoader
            height="100%"
            width={127}
            style={{ marginRight: '20px' }}
          />
        }
        unloader={<TourImage src={defaultImage} />}
      />
      <TourInfo>
        <TourName lines={2}>{name}</TourName>
        {(nameAndAddress || isOnline) && (
          <LocationInfo>
            {placeIcon} {locationLabel}
          </LocationInfo>
        )}
      </TourInfo>
      {renderActionSection()}
    </Card>
  );
};

export default withRouter(ExperienceCard);
